import React, { useState, useMemo, useEffect } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Box,
    Typography,
    Modal,
    Chip,
    TextField,
    Container,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Grid,
} from "@mui/material/";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import EditIcon from "@mui/icons-material/Edit";
import EventIcon from "@mui/icons-material/Event";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import actions from "../../Store/actions";
import { format } from "../../common/lib";
import styles from "./styles.js";
import ListDetail from "../ListDetail";
import { ActionBar } from "../../common/components";
import {
    useAudit,
    useToaster,
    useAuditProgressDetails,
    useGroupAuditProgressDetails,
    useGroupAuditValidation,
    useGroupedAudits,
} from "../../common/hooks";
import DeviceSystemPermissions from "../DeviceSystemPermissions";
import AuditProgress from "./AuditProgress";
import CameraLocationError from "../../common/components/CameraLocationError";
import AuditLocations from "./AuditLocations";
import { logToSentry } from "../../config/Sentry";
import {
    FolderOutlined as FolderOutlinedIcon,
    FolderSpecialOutlined as FolderSpecialOutlinedIcon,
} from "@mui/icons-material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";

const GroupedAudits = () => {
    const [loading, setLoading] = useState(false);
    const [messageIsShown, setMessageIsShown] = useState(false);
    const [permissions, setPermissions] = useState(false); // true = all good
    const [openLocationList, setOpenLocationList] = useState(false);
    const [searchText, setSearchText] = useState(""); // State for search input
    const [expanded, setExpanded] = useState(false);

    let navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { auditId } = useParams();
    const audit = useAudit(auditId);
    const toast = useToaster();
    const { isComplete, alt, assigned, required, auditStarted } =
        useAuditProgressDetails("db", auditId);

    const { groupedUnexpectedAsset, groupedAssignedAsset, groupedRequired } =
        useGroupAuditProgressDetails("db", auditId);

    const { audits, typesList } = useSelector((state) => state);

    /** On hide/show location */
    const handleOpenLocation = () => setOpenLocationList(true);
    const handleCloseLocation = () => setOpenLocationList(false);
    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    /** On hide/show  message */
    const showMessage = () => setMessageIsShown(true);
    const hideMessage = () => setMessageIsShown(false);

    const showSubmitGroupButton = groupedRequired === 0 ? true : false;
    const disableGroupAuditSubmit = useGroupAuditValidation(
        assigned,
        auditId,
        "button",
        groupedRequired
    );

    const singleButtonLayout = auditStarted === true ? true : false;
    const groupedAudits = useGroupedAudits();
    const currentLocation = useMemo(() => {
        return groupedAudits.find(
            (item) => item.audit.id === parseInt(auditId)
        );
    }, [auditId, groupedAudits]);

    // Flatten the array to extract relevant options
    const flattenedGroupAudits = useMemo(() => {
        const getBranchTypeName = (branchId) => {
            const res = typesList.branchTypes.data.find(
                (item) => item.id === branchId
            );
            return res ? `${res.name} - ${res.ref}` : "";
        };
        return groupedAudits.map((item, index) => ({
            id: index,
            audit: item.audit,
            auditId: item.audit.id,
            locationName: item.location.name,
            branchType: item.audit.brandOperation
                ? getBranchTypeName(item.audit.brandOperation.branchType)
                : "",
            brandOperationCode: item.audit.brandOperation
                ? item.audit.brandOperation.brandOperationCode
                : "",
        }));
    }, [typesList, groupedAudits]);

    // Filter the options based on the search text
    const filterGroupAudits = useMemo(() => {
        if (!searchText) return flattenedGroupAudits;
        return flattenedGroupAudits.filter(
            (item) =>
                item.locationName
                    .toLowerCase()
                    .includes(searchText.toLowerCase()) ||
                item.branchType.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [flattenedGroupAudits, searchText]);

    // Handle location selection and close modal
    const handleLocationClick = (auditId, audit) => {
        try {
            const url =
                audit.auditReference.toLowerCase() === "none"
                    ? `/auth/audits/${auditId}/details`
                    : `/auth/audits/${auditId}/groupedAudits`;
            dispatch(actions.audits.create.itemSet(audit));
            navigate(url, { state: { showModal: false } });
            setSearchText("");
            handleCloseLocation(); // Close modal after selection
        } catch (error) {
            console.log(error);
            toast.error(t("errors.locationChange"));
            logToSentry("location change", {
                error,
            });
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchAuditDetails = async () => {
            setLoading(true);
            try {
                if (!audits.item)
                    dispatch(actions.audits.create.itemSet(audit));
                dispatch(actions.auditCompletion.create.init(auditId));
                await dispatch(actions.assetSync.create.postRequest());
                dispatch(actions.auditUpload.create.statusInit());
            } catch (error) {
                console.error("Error fetching audit details:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAuditDetails();
    }, [audits, auditId]);

    const createButton = (
        clickFn: Function,
        loading: Boolean,
        title: string,
        variantType: Boolean,
        singleButtonLayout
    ) => (
        <Grid item xs={singleButtonLayout ? 6 : 12}>
            <Button
                disabled={loading}
                fullWidth
                variant={variantType === false ? "outlined" : "contained"}
                loading={loading.toString()}
                onClick={clickFn}
            >
                {title}
            </Button>
        </Grid>
    );

    const onSubmitPress = () => {
        navigate(`/auth/audits/${auditId}/submit`);
    };

    const onSubmitAllJobs = () => {
        navigate(`/auth/audits/${auditId}/groupAuditSubmission`);
    };

    const onStartContinuePress = async () => {
        try {
            showMessage();
            setLoading(true);
            await dispatch(actions.geoLocation.create.getRequest());
            navigate(`/auth/audits/${auditId}/assets/list`);
        } catch (error) {
            console.log(error);
            const link = (
                <CameraLocationError message={t("errors.geoLocationGet")} />
            );
            toast.error(link);

            hideMessage();
            setLoading(false);
            logToSentry("onStartContinuePress", {
                error,
            });
            return;
        }
    };

    if (!audit)
        return (
            <Box sx={styles.spinnerContainer}>
                <CircularProgress />
            </Box>
        );

    return (
        <>
            <Container sx={styles.zoneContainer}>
                {audit && (
                    <>
                        <List sx={styles.listContainer}>
                            <ListDetail
                                icon={
                                    <EditIcon
                                        onClick={handleOpenLocation}
                                        color="primary"
                                    />
                                }
                                primaryText={
                                    <AuditLocations
                                        auditId={auditId}
                                        onOpenList={handleOpenLocation}
                                    />
                                }
                            />
                            <ListDetail
                                icon={
                                    <DescriptionOutlinedIcon color="primary" />
                                }
                                primaryText={
                                    <Typography variant="caption">
                                        {currentLocation.audit.name}
                                    </Typography>
                                }
                            />
                            <ListDetail
                                icon={<PlaceOutlinedIcon color="primary" />}
                                primaryText={
                                    <Typography variant="caption">
                                        {currentLocation.location.name}
                                    </Typography>
                                }
                            />
                            <Accordion
                                expanded={expanded === "panel1"}
                                onChange={handleAccordionChange("panel1")}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="panel1bh-content"
                                    id="panel1bh-header"
                                >
                                    <Typography
                                        component="span"
                                        sx={{ width: "33%", flexShrink: 0 }}
                                    >
                                        <FolderSpecialOutlinedIcon size="medium" />
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{ color: "text.secondary" }}
                                    >
                                        {t("totalAssets")}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{ justifySelf: "center" }}
                                >
                                    <AuditProgress
                                        alt={groupedUnexpectedAsset}
                                        assigned={groupedAssignedAsset}
                                        required={groupedRequired}
                                    />
                                </AccordionDetails>
                            </Accordion>
                            <Accordion
                                expanded={expanded === "panel2"}
                                onChange={handleAccordionChange("panel2")}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="panel1bh-content"
                                    id="panel1bh-header"
                                >
                                    <Typography
                                        component="span"
                                        sx={{ width: "33%", flexShrink: 0 }}
                                    >
                                        <FolderOutlinedIcon size="medium" />
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{ color: "text.secondary" }}
                                    >
                                        {t("currentLocation")}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{ justifySelf: "center" }}
                                >
                                    <AuditProgress
                                        alt={alt}
                                        assigned={assigned}
                                        required={required}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        </List>

                        <DeviceSystemPermissions
                            setPermissions={setPermissions}
                        />
                        <ActionBar>
                            <Grid container spacing={2}>
                                {auditStarted && (
                                    <>
                                        {createButton(
                                            onSubmitPress,
                                            loading,
                                            t(
                                                isComplete
                                                    ? "submitAudit"
                                                    : "submitAudit" //submitPartialAudit
                                            ),
                                            isComplete,
                                            singleButtonLayout
                                        )}
                                    </>
                                )}
                                {createButton(
                                    onStartContinuePress,
                                    loading || !permissions,
                                    t(
                                        auditStarted
                                            ? "continueAudit"
                                            : "startAudit"
                                    ),
                                    !isComplete,
                                    singleButtonLayout
                                )}
                                {showSubmitGroupButton === 0 ? (
                                    <Grid item xs={12}>
                                        <Button
                                            disabled={
                                                loading ||
                                                !permissions ||
                                                disableGroupAuditSubmit
                                            }
                                            fullWidth
                                            variant="contained"
                                            loading={loading.toString()}
                                            onClick={onSubmitAllJobs}
                                            sx={styles.buttons}
                                        >
                                            {t("submitZone")}
                                        </Button>
                                    </Grid>
                                ) : null}
                            </Grid>
                        </ActionBar>
                    </>
                )}
                <Modal
                    open={messageIsShown}
                    onClose={hideMessage}
                    aria-labelledby="message-modal"
                    aria-describedby="preparing-audit-modal"
                >
                    <Box sx={styles.modalBox}>
                        <Typography
                            id="modal-modal-description"
                            sx={styles.progress}
                        >
                            {t("messages.preparingAudit")}
                        </Typography>
                        <CircularProgress />
                    </Box>
                </Modal>
            </Container>
            {/* Modal */}
            <Modal
                open={openLocationList}
                onClose={(event, reason) => {
                    if (reason && reason === "backdropClick") return;
                    handleCloseLocation();
                }}
                aria-labelledby="location-list-modal-title"
                aria-describedby="location-list-modal-description"
            >
                <Box sx={styles.locationModal}>
                    {/* Search Input */}
                    <TextField
                        label={t("search")}
                        variant="outlined"
                        fullWidth
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)} // Update search state
                        sx={{ mb: 2 }} // Margin bottom for spacing
                    />

                    {/* List of filtered students */}
                    <List sx={styles.locationList}>
                        {filterGroupAudits.length > 0 ? (
                            filterGroupAudits.map((item) => (
                                <ListItem
                                    key={`${item.auditId}-${item.locationName}`}
                                    sx={styles.locationItem}
                                    onClick={() =>
                                        handleLocationClick(
                                            item.auditId,
                                            item.audit
                                        )
                                    } // Handle location click
                                >
                                    <ListItemText
                                        primary={
                                            <>
                                                <Typography
                                                    component="div"
                                                    sx={{ mb: 1 }}
                                                    variant="caption"
                                                >
                                                    {item.locationName}
                                                </Typography>
                                                {item.branchType &&
                                                    item.branchType.length >
                                                        0 && (
                                                        <Chip
                                                            variant="outlined"
                                                            sx={{
                                                                textTransform:
                                                                    "capitalize",
                                                            }}
                                                            label={
                                                                item.branchType
                                                            }
                                                            variant="outlined"
                                                        />
                                                    )}
                                            </>
                                        }
                                        secondary={<></>}
                                    />
                                </ListItem>
                            ))
                        ) : (
                            <ListItem>
                                <ListItemText
                                    primary={t("messages.noLocationFound")}
                                />
                            </ListItem>
                        )}
                    </List>
                </Box>
            </Modal>
        </>
    );
};

export default GroupedAudits;

/** TODO
 * 1. Test cases
 * 2. Sycnhronus error on console
 */
